import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch } from '@/lib/api';
import SceneLayout from '@/components/scene/SceneLayout';
import Aperture from '@/components/scene/Aperture';

const FAQ = ['q1', 'q2', 'q3', 'q4'] as const;

export default function Support() {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const isAuthenticated = !!localStorage.getItem('token');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isAuthenticated) {
        const workspaceId = localStorage.getItem('workspaceId');
        if (workspaceId) {
          await apiFetch(`/workspaces/${workspaceId}/support`, {
            method: 'POST',
            body: JSON.stringify({ subject, description: message }),
          });
        } else {
          await apiFetch('/contact', {
            method: 'POST',
            body: JSON.stringify({ type: 'support', name, email, phone, subject, message }),
          });
        }
      } else {
        await apiFetch('/contact', {
          method: 'POST',
          body: JSON.stringify({ type: 'support', name, email, phone, subject, message }),
        });
      }

      toast.success(t('support.success'));
      setName('');
      setEmail('');
      setPhone('');
      setSubject('');
      setMessage('');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('support.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <SceneLayout>
      {/* Front desk — the direct lines sit pinned beside the ticket form */}
      <section className="wrap pt-[clamp(48px,10vh,120px)] pb-[var(--section)]">
        <div className="split-golden">
          <div className="sticky-col grid gap-6 content-start" data-rv="dissolve">
            <p className="mono">{t('support.label')}</p>
            <h1 className="display">{t('support.title')}</h1>
            <p className="body max-w-[36ch]">{t('support.subtitle')}</p>
            <dl className="desk">
              <div>
                <dt className="mono">{t('support.email_label')}</dt>
                <dd><a href="mailto:contact@targetna.com" className="link-line">contact@targetna.com</a></dd>
              </div>
              <div>
                <dt className="mono">{t('support.phone_label')}</dt>
                <dd><a href="tel:+201228351416" className="link-line" dir="ltr">+20 122 835 1416</a></dd>
              </div>
            </dl>
          </div>

          <Aperture ticks rv="aperture" label={t('support.form_label')}>
            <form onSubmit={handleSubmit} className="grid gap-7 p-[clamp(24px,4vw,56px)]">
              {!isAuthenticated && (
                <>
                  <div className="grid md:grid-cols-2 gap-7">
                    <label className="field" htmlFor="name">
                      <span className="mono">{t('support.name')}</span>
                      <input id="name" placeholder={t('support.name_ph')} value={name} onChange={(e) => setName(e.target.value)} required />
                    </label>
                    <label className="field" htmlFor="email">
                      <span className="mono">{t('support.email')}</span>
                      <input id="email" type="email" placeholder="your@email.com" value={email} onChange={(e) => setEmail(e.target.value)} required />
                    </label>
                  </div>
                  <label className="field" htmlFor="phone">
                    <span className="mono">{t('support.phone_optional')}</span>
                    <input id="phone" type="tel" dir="ltr" placeholder="+20 xxx xxx xxxx" value={phone} onChange={(e) => setPhone(e.target.value)} />
                  </label>
                </>
              )}
              <label className="field" htmlFor="subject">
                <span className="mono">{t('support.subject')}</span>
                <input id="subject" placeholder={t('support.subject_ph')} value={subject} onChange={(e) => setSubject(e.target.value)} required />
              </label>
              <label className="field" htmlFor="message">
                <span className="mono">{t('support.message')}</span>
                <textarea id="message" rows={7} placeholder={t('support.message_ph')} value={message} onChange={(e) => setMessage(e.target.value)} required />
              </label>
              <button type="submit" className="btn-ink w-full" disabled={loading}>
                {loading ? t('support.submitting') : t('support.submit')}
                {!loading && <ArrowRight className="btn-arrow h-4 w-4" />}
              </button>
            </form>
          </Aperture>
        </div>
      </section>

      {/* Questions — a hairline schedule, like the feature list on How It Works */}
      <section className="wrap pb-[var(--section)]">
        <div className="split-golden">
          <div className="grid gap-4 content-start">
            <p className="mono">S-05</p>
            <h2 className="display-m">{t('support.faq_title')}</h2>
          </div>
          <div data-rv="line">
            <div className="ledger__head" />
            {FAQ.map((q, i) => (
              <div key={q} className="relative">
                <details className="fx">
                  <summary>
                    <span className="mono">{String(i + 1).padStart(2, '0')}</span>
                    <span className="text-[17px] font-medium">{t(`support.faq.${q}.q`)}</span>
                    <span className="fx__plus" aria-hidden="true">+</span>
                  </summary>
                  <div className="fx__body fx__body--one">
                    <p className="body text-[15px]">{t(`support.faq.${q}.a`)}</p>
                  </div>
                </details>
                <span className="rv-line" style={{ ['--i' as string]: i }} />
              </div>
            ))}
          </div>
        </div>
      </section>
    </SceneLayout>
  );
}
